import React, { Component } from "react";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import { Alert, Modal, Table, Tag } from "antd";
import ImportadorExcelGenerico from "../../../../Components/ImportadorExcelGenerico";
import SelectClientesItem from "../../../../Components/SelectClientesItem";
import { ESTATUS_EQUIPO } from "../cmmsComun";
import "../cmms.css";

// Importar el inventario de equipos de un cliente desde Excel (ej. el de ISSEMYM o
// Santa Catarina). El navegador valida y resuelve tipo/marca; el backend revalida,
// resuelve la sucursal por nombre y reserva los folios.

const normalizar = (t) =>
    String(t ?? "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

const SIN_SERIE = ["s/s", "s/n", "sin no. serie", "sin numero de serie", "sin serie", "n/a", "na", "-"];

// Fecha del Excel: numero serial, dd/mm/aaaa o aaaa-mm-dd -> "aaaa-mm-dd" (o null).
const fechaExcel = (v) => {
    if (v === undefined || v === null || v === "") return { ok: true, valor: null };
    if (typeof v === "number") {
        const d = new Date(Math.round((v - 25569) * 86400 * 1000));
        return isNaN(d.getTime()) ? { ok: false } : { ok: true, valor: d.toISOString().slice(0, 10) };
    }
    const t = String(v).trim();
    let m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) return { ok: true, valor: `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` };
    m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) return { ok: true, valor: `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}` };
    return { ok: false };
};

const COLUMNAS = [
    "Categoria",
    "Tipo de activo",
    "Marca",
    "Modelo",
    "No. Serie",
    "No. Inventario",
    "ID Externo",
    "ID Interno",
    "Sucursal",
    "Ubicacion",
    "Area",
    "Estatus",
    "Pertenencia",
    "Jerarquia",
    "Tiempo max respuesta (hrs)",
    "Fecha instalacion",
    "Inicio garantia",
    "Fin garantia",
    "Descripcion",
];

const columnasPreview = [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Categoría", dataIndex: "categoria", key: "categoria", width: 130 },
    { title: "Tipo", dataIndex: "_tipoNombre", key: "_tipoNombre", width: 170 },
    { title: "Marca", dataIndex: "_marcaNombre", key: "_marcaNombre", width: 120 },
    { title: "Modelo", dataIndex: "modelo", key: "modelo", width: 120 },
    { title: "Serie", dataIndex: "numeroSerie", key: "numeroSerie", width: 130, render: (v, r) => (r.sinSerie ? <span style={{ color: "#8c8c8c" }}>Sin serie</span> : v) },
    { title: "Inventario", dataIndex: "numeroInventario", key: "numeroInventario", width: 110 },
    { title: "Sucursal", dataIndex: "sucursal", key: "sucursal", width: 150 },
    { title: "Ubicación", dataIndex: "ubicacion", key: "ubicacion", width: 120 },
    { title: "Área", dataIndex: "area", key: "area", width: 100 },
    { title: "Estatus", dataIndex: "_estatusNombre", key: "_estatusNombre", width: 120 },
    { title: "Pertenencia", dataIndex: "pertenencia", key: "pertenencia", width: 140 },
];

const construirPlantilla = (catalogos) => {
    const wb = XLSX.utils.book_new();
    const ejemplo = [
        {
            Categoria: catalogos?.categorias?.[0] || "Equipo médico",
            "Tipo de activo": catalogos?.tipos?.[0]?.nombre || "Ventilador Mecánico",
            Marca: catalogos?.marcas?.[0]?.nombre || "Viasys",
            Modelo: "AVEA",
            "No. Serie": "BDV01144",
            "No. Inventario": "1231271",
            "ID Externo": "AST10000098818",
            "ID Interno": "",
            Sucursal: "",
            Ubicacion: "Quirófanos",
            Area: "Qx 1",
            Estatus: "Funcional",
            Pertenencia: catalogos?.pertenencias?.[0] || "Propiedad del cliente",
            Jerarquia: "Por definir",
            "Tiempo max respuesta (hrs)": 24,
            "Fecha instalacion": "15/01/2024",
            "Inicio garantia": "",
            "Fin garantia": "",
            Descripcion: "",
        },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ejemplo, { header: COLUMNAS }), "Activos");
    // Hojas de referencia con los valores validos.
    XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet((catalogos?.tipos || []).map((t) => ({ "Tipo de activo": t.nombre, "Nombres alternos": (t.aliasImportacion || []).join(", ") }))),
        "Tipos de activo"
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet((catalogos?.marcas || []).map((m) => ({ Marca: m.nombre }))), "Marcas");
    const listas = [];
    const max = Math.max((catalogos?.categorias || []).length, (catalogos?.pertenencias || []).length, (catalogos?.jerarquias || []).length, ESTATUS_EQUIPO.length);
    for (let i = 0; i < max; i++)
        listas.push({
            Categoria: catalogos?.categorias?.[i] || "",
            Pertenencia: catalogos?.pertenencias?.[i] || "",
            Jerarquia: catalogos?.jerarquias?.[i] || "",
            Estatus: ESTATUS_EQUIPO[i]?.label || "",
        });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(listas), "Listas");
    return wb;
};

// Indices por nombre normalizado (y alias para los tipos).
const prepararCatalogos = (data) => {
    const tiposPorNombre = new Map();
    (data.tipos || []).forEach((t) => {
        tiposPorNombre.set(normalizar(t.nombre), t);
        (t.aliasImportacion || []).forEach((a) => tiposPorNombre.set(normalizar(a), t));
    });
    const marcasPorNombre = new Map((data.marcas || []).map((m) => [normalizar(m.nombre), m]));
    return {
        ...data,
        tiposPorNombre,
        marcasPorNombre,
        categoriasPorNombre: new Map((data.categorias || []).map((c) => [normalizar(c), c])),
        pertenenciasPorNombre: new Map((data.pertenencias || []).map((c) => [normalizar(c), c])),
        jerarquiasPorNombre: new Map((data.jerarquias || []).map((c) => [normalizar(c), c])),
    };
};

const ESTATUS_POR_NOMBRE = new Map([
    ...ESTATUS_EQUIPO.map((e) => [normalizar(e.label), e.value]),
    ["condicionado", 2],
    ["funcional condicional", 2],
    ["atencion", 2],
    ["disfuncional", 3],
    ["danado", 3],
]);

const validarFila = (fila, cat, _indice, contexto) => {
    const errores = [];
    const get = (col) => (fila[col] === undefined || fila[col] === null ? "" : String(fila[col]).trim());

    const categoria = cat.categoriasPorNombre.get(normalizar(get("Categoria")));
    if (!get("Categoria")) errores.push({ campo: "Categoria", motivo: "está vacía" });
    else if (!categoria) errores.push({ campo: "Categoria", motivo: `"${get("Categoria")}" no está en la lista de Categorías (Configuración)` });

    const tipo = cat.tiposPorNombre.get(normalizar(get("Tipo de activo")));
    if (!get("Tipo de activo")) errores.push({ campo: "Tipo de activo", motivo: "está vacío" });
    else if (!tipo) errores.push({ campo: "Tipo de activo", motivo: `"${get("Tipo de activo")}" no existe (dé de alta el tipo o agréguelo como nombre alterno)` });

    const marca = cat.marcasPorNombre.get(normalizar(get("Marca")));
    if (!get("Marca")) errores.push({ campo: "Marca", motivo: "está vacía" });
    else if (!marca) errores.push({ campo: "Marca", motivo: `"${get("Marca")}" no existe en Marcas` });

    if (!get("Modelo")) errores.push({ campo: "Modelo", motivo: "está vacío" });

    const serieTexto = get("No. Serie");
    const sinSerie = !serieTexto || SIN_SERIE.includes(serieTexto.toLowerCase());

    const pertenencia = cat.pertenenciasPorNombre.get(normalizar(get("Pertenencia")));
    if (!get("Pertenencia")) errores.push({ campo: "Pertenencia", motivo: "está vacía" });
    else if (!pertenencia) errores.push({ campo: "Pertenencia", motivo: `"${get("Pertenencia")}" no está en la lista de Pertenencias (Configuración)` });

    const jerarquiaTexto = get("Jerarquia");
    const jerarquia = jerarquiaTexto ? cat.jerarquiasPorNombre.get(normalizar(jerarquiaTexto)) : "Por definir";
    if (jerarquiaTexto && !jerarquia) errores.push({ campo: "Jerarquia", motivo: `"${jerarquiaTexto}" no está en la lista de Jerarquías` });

    const estatusTexto = get("Estatus");
    const estatus = estatusTexto ? ESTATUS_POR_NOMBRE.get(normalizar(estatusTexto)) : 1;
    if (estatusTexto && !estatus) errores.push({ campo: "Estatus", motivo: `"${estatusTexto}" no es válido (Funcional, Funcional condicionado, Dañado, Baja)` });

    const tiempo = get("Tiempo max respuesta (hrs)");
    if (tiempo && !(Number(tiempo) >= 0)) errores.push({ campo: "Tiempo max respuesta (hrs)", motivo: "debe ser un número" });

    const fechas = {};
    for (const [col, clave] of [["Fecha instalacion", "fechaInstalacion"], ["Inicio garantia", "inicioGarantia"], ["Fin garantia", "finGarantia"]]) {
        const f = fechaExcel(fila[col]);
        if (!f.ok) errores.push({ campo: col, motivo: "no es una fecha válida (use dd/mm/aaaa)" });
        else fechas[clave] = f.valor;
    }

    // Serie repetida dentro del mismo archivo (mismo tipo).
    if (!sinSerie && tipo) {
        const llave = `${tipo._id}|${normalizar(serieTexto)}`;
        if (contexto.clavesYaUsadas.has(llave))
            errores.push({ campo: "No. Serie", motivo: `serie repetida en el archivo (también en la fila ${contexto.clavesYaUsadas.get(llave)})` });
        else contexto.clavesYaUsadas.set(llave, (fila["__rowNum__"] ?? 0) + 1);
    }

    if (errores.length) return { ok: false, errores };
    return {
        ok: true,
        datos: {
            numeroFila: (fila["__rowNum__"] ?? 0) + 1,
            categoria,
            tipoEquipoId: tipo._id,
            _tipoNombre: tipo.nombre,
            marcaId: marca._id,
            _marcaNombre: marca.nombre,
            modelo: get("Modelo"),
            numeroSerie: sinSerie ? "" : serieTexto,
            sinSerie,
            numeroInventario: get("No. Inventario"),
            idExterno: get("ID Externo"),
            idInterno: get("ID Interno"),
            sucursal: get("Sucursal"),
            ubicacion: get("Ubicacion"),
            area: get("Area"),
            estatusEquipo: estatus,
            _estatusNombre: ESTATUS_EQUIPO.find((e) => e.value === estatus)?.label,
            pertenencia,
            jerarquia,
            tiempoMaximoRespuestaHrs: tiempo ? Number(tiempo) : null,
            descripcion: get("Descripcion"),
            ...fechas,
        },
    };
};

class ImportarActivos extends Component {
    state = { clienteId: null, resultado: null };

    render() {
        const resultados = this.state.resultado?.resultados || [];
        const errores = resultados.filter((r) => r.estado === "error");
        return (
            <>
                <ImportadorExcelGenerico
                    {...this.props}
                    titulo="Importar Activos"
                    nombreEntidadPlural="activos"
                    endpointDatosImportar="cmms/datosImportarActivosCMMS"
                    endpointImportar="cmms/importarActivosCMMS"
                    nombrePlantilla="plantillaActivosCMMS.xlsx"
                    prepararCatalogos={prepararCatalogos}
                    construirPlantilla={construirPlantilla}
                    validarFila={validarFila}
                    columnasPreview={columnasPreview}
                    tamanoLote={300}
                    prepararPayloadExtra={() => ({ clienteId: this.state.clienteId })}
                    instrucciones={[
                        "Descargue la plantilla: la hoja “Activos” trae las columnas; las otras hojas listan los tipos, marcas y opciones válidas.",
                        "Obligatorios: Categoría, Tipo de activo, Marca, Modelo y Pertenencia. Si el equipo no tiene serie, deje la columna vacía o escriba S/S.",
                        "El Tipo de activo se reconoce también por sus nombres alternos (Tipos de Activo › Nombres alternos), y la Marca debe existir en Marcas.",
                        "Elija el cliente dueño de los equipos (opcional). La columna Sucursal debe coincidir con el nombre de una dirección de ese cliente.",
                        "Con cliente, Ubicación y Área vacías quedan como “General”. Los folios (AST…) se asignan al importar.",
                    ]}
                    controlesExtra={
                        <div style={{ maxWidth: 520, margin: "10px 0" }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: "#4a5866", marginBottom: 4 }}>Cliente dueño de los equipos (opcional)</div>
                            <SelectClientesItem
                                empresaId={this.props.empresaId}
                                headersToken={this.props.headersToken}
                                width="100%"
                                placeholder="Sin cliente"
                                allowClear
                                value={this.state.clienteId || undefined}
                                onChange={(clienteId) => this.setState({ clienteId: clienteId || null })}
                            />
                        </div>
                    }
                    onImportSuccess={(resData) => {
                        this.setState({ resultado: resData });
                        return true;
                    }}
                />
                <Modal
                    className="cmms-modal"
                    visible={!!this.state.resultado}
                    title={<div className="cmms-modal-titulo-texto">Resultado de la importación</div>}
                    width={window.innerWidth > 900 ? 820 : "96%"}
                    onCancel={() => this.setState({ resultado: null })}
                    onOk={() => this.props.history.push("/panel/cmmsActivos")}
                    okText="Ir a Activos"
                    cancelText="Seguir importando"
                >
                    <div style={{ padding: 16 }}>
                        <Alert
                            type={errores.length ? "warning" : "success"}
                            showIcon
                            message={`${this.state.resultado?.numero || 0} activo(s) creados${errores.length ? ` · ${errores.length} renglón(es) no se importaron` : ""}`}
                            style={{ marginBottom: 12 }}
                        />
                        {errores.length > 0 && (
                            <Table
                                size="small"
                                rowKey={(r, i) => `${r.numeroFila}-${i}`}
                                pagination={{ pageSize: 10 }}
                                dataSource={errores}
                                columns={[
                                    { title: "Fila", dataIndex: "numeroFila", width: 60 },
                                    { title: "Serie", dataIndex: "serie", width: 140 },
                                    { title: "Motivo", dataIndex: "motivo", render: (v) => <Tag color="orange" style={{ whiteSpace: "normal" }}>{v}</Tag> },
                                ]}
                            />
                        )}
                    </div>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportarActivos);
